import React, {useContext, useMemo, useState} from 'react';
import {useRaceDataWebSocket} from '../../../hooks/useRaceDataWebSocket';
import {TeamDataContext} from '../../../context/TeamData.context';
import UseCountryFlag from '../../../hooks/useCountryFlag';
import './ResultsTable.scss';
import {CircularProgress} from '@mui/material';
import {CurrentRaceEventContext} from "../../../context/currentRaceEvent.context";
import {OrdRaceStatusEnum} from "../../../models/enums/ordRaceStatusEnum";
import {LoginContext} from "../../../context/login.context";
import {formatPracticeRaceName} from "../../../utils/helpers/formatRaceName";
import {SortDirection} from "../../../models/tableTypes";
import {DateTime} from "luxon";
import {TypeOfRaceEnum} from "../../../models/raceDataModel";

interface ResultsTableProps {
    eventId: string;
}

interface TeamResults {
    country: string;
    racePositions: (number | null)[];
    racePoints: (number | null)[];
    totalPoints: number;
    penaltyPoints: number;
    finalPosition: number;
}

type SortColumn = 'totalPoints' | 'finalPosition' | `race-${number}`;

const CountryFlagWrapper: React.FC<{ country: string }> = ({ country }) => {
    return UseCountryFlag(country, { width: '24px', height: '16px' });
};

const ResultsTable: React.FC<ResultsTableProps> = ({eventId}) => {
    const {raceType} = useContext(CurrentRaceEventContext);
    const {isVip, isInternalUser} = useContext(LoginContext)
    const includePracticeRaces = (isVip || isInternalUser)
    const {
        raceData,
        connectionState
    } = useRaceDataWebSocket(eventId, includePracticeRaces, true, raceType !== 'live');
    const {getTeamByCountryCode, teams} = useContext(TeamDataContext);
    const [sortColumn, setSortColumn] = useState<SortColumn>('finalPosition');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const populatedRaces = useMemo(() => {
        if (raceData?.fleetRaceResults) {
            return raceData.fleetRaceResults
        }
    }, [raceData?.fleetRaceResults])


    const processedResults = useMemo(() => {
        if (!populatedRaces?.length) return null;

        const hasAnyRaceData = populatedRaces?.some(
            race => race.name !== "No Race Data"
        );

        if (!hasAnyRaceData) {
            return null;
        }

        const teamResultsMap = new Map<string, TeamResults>();
        const allCountries = new Set<string>();

        populatedRaces?.forEach((race) => {
            if (race.winners && race.winners.length > 0) {
                race.winners.forEach((team) => {
                    if (team.country !== 'GEN') {
                        allCountries.add(team.country);
                    }
                });
            }
        });

        raceData.raceToTheFinal?.forEach((team) => {
            if (team.country !== 'GEN') {
                allCountries.add(team.country);
            }
        });

        if (allCountries.size === 0 && teams && teams.length > 0) {
            teams.forEach((team) => {
                if (team.code && team.code !== 'GEN') {
                    allCountries.add(team.code);
                }
            });
        }

        allCountries.forEach((country) => {
            teamResultsMap.set(country, {
                country,
                racePositions: [],
                racePoints: [],
                totalPoints: 0,
                penaltyPoints: 0,
                finalPosition: 0,
            });
        });

        populatedRaces?.forEach((race) => {
            if (race.winners && race.winners.length > 0) {
                const raceWinners = race.winners;

                teamResultsMap.forEach((teamResult, country) => {
                    const position = raceWinners.findIndex((w) => w.country === country) + 1;
                    const teamData = raceWinners.find((w) => w.country === country);

                    if (position > 0 && teamData) {
                        teamResult.racePositions.push(position);
                        teamResult.racePoints.push(teamData.points);
                    } else {
                        teamResult.racePositions.push(null);
                        teamResult.racePoints.push(null);
                    }
                });
            } else {
                teamResultsMap.forEach((teamResult) => {
                    teamResult.racePositions.push(null);
                    teamResult.racePoints.push(null);
                });
            }
        });

        const finalStandings = raceData.raceToTheFinal || [];
        finalStandings.forEach((standing, index) => {
            const teamResult = teamResultsMap.get(standing.country);
            if (teamResult) {
                teamResult.totalPoints = standing.points;
                teamResult.penaltyPoints = standing.penaltyPoints;
                teamResult.finalPosition = index + 1;
            }
        });

        let nextPosition = finalStandings.length + 1;
        teamResultsMap.forEach((teamResult) => {
            if (teamResult.finalPosition === 0) {
                teamResult.finalPosition = nextPosition++;
            }
        });

        return Array.from(teamResultsMap.values());
    }, [raceData, teams]);

    const sortedResults = useMemo(() => {
        if (!processedResults) return [];

        return [...processedResults].sort((a, b) => {
            let aValue: number | null = null;
            let bValue: number | null = null;

            if (sortColumn === 'totalPoints') {
                aValue = a.totalPoints;
                bValue = b.totalPoints;
            } else if (sortColumn === 'finalPosition') {
                aValue = a.finalPosition;
                bValue = b.finalPosition;
            } else if (sortColumn.startsWith('race-')) {
                const raceIndex = parseInt(sortColumn.split('-')[1]);
                aValue = a.racePositions[raceIndex] || null;
                bValue = b.racePositions[raceIndex] || null;
            }

            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;

            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        });
    }, [processedResults, sortColumn, sortDirection]);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection(column === 'totalPoints' ? 'desc' : 'asc');
        }
    };

    const getRaceSubHeader = (raceIndex: number): string => {
        const race = populatedRaces?.[raceIndex];
        if (!race) return 'Pos (Pts)';

        // Show start time for races that haven't started yet
        if (race.status === OrdRaceStatusEnum.Future || race.status === OrdRaceStatusEnum.Preparatory) {
            try {
                // Parse the start time (format: "2026-01-17 05:09:00.000")
                const dateTimeStr = race.start.replace(' ', 'T');
                const startTime = DateTime.fromISO(dateTimeStr)
                    .setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
                return startTime.toFormat('HH:mm');
            } catch (e) {
                return 'Pos (Pts)';
            }
        }

        if (populatedRaces[raceIndex].name === TypeOfRaceEnum.Final) return 'Pos'

        return 'Pos (Pts)';
    };

    const isLoadingRaceData = !raceData && (connectionState.status === 'connecting' || connectionState.status === 'initial' || connectionState.status === 'connected');
    const isLoadingTeamData = !teams || teams.length === 0;

    if (isLoadingRaceData || isLoadingTeamData) {
        return (
            <div className="results-table-loading">
                <CircularProgress size={70} />
            </div>
        );
    }

    if (connectionState.status === 'error') {
        return <div className="results-table-error">Error: {connectionState.error}</div>;
    }

    const raceCount = populatedRaces?.length || 0;

    const getSortIndicator = (column: SortColumn) => {
        if (sortColumn !== column) return '';
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <div className="results-table-container">
            <div className="results-table-wrapper">
                <table className="results-table">
                    <thead>
                    <tr>
                        <th className="sticky-col country-col">Team</th>
                        {Array.from({length: raceCount}, (race, i) => {
                            const raceTitle = formatPracticeRaceName(populatedRaces?.[i].name, includePracticeRaces)
                            const raceTitleWithoutYear = raceTitle.replace(/\s+(20\d{2}|Season\s+\d+)$/i, '')
                            return <th
                                key={`race-${i}`}
                                className="race-col sortable"
                                onClick={() => handleSort(`race-${i}` as SortColumn)}
                            >
                                {raceTitleWithoutYear}{getSortIndicator(`race-${i}` as SortColumn)}
                            </th>
                        })}
                        <th
                            className="total-col sortable"
                            onClick={() => handleSort('totalPoints')}
                        >
                            Total{getSortIndicator('totalPoints')}
                        </th>
                        <th
                            className="position-col sortable"
                            onClick={() => handleSort('finalPosition')}
                        >
                            Rank{getSortIndicator('finalPosition')}
                        </th>
                    </tr>
                    <tr>
                        <th className="sticky-col"></th>
                        {Array.from({length: raceCount}, (_, i) => (
                            <th key={`sub-${i}`} className="sub-header">{getRaceSubHeader(i)}</th>
                        ))}
                        <th></th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedResults.map((team) => {
                        const teamData = getTeamByCountryCode(team.country);
                        const teamColor = teamData?.primaryTeamHexColour || '#66c1c4';

                        return (
                            <tr
                                key={team.country}
                                style={{'--team-color': teamColor} as React.CSSProperties}
                            >
                                <td className="sticky-col country-cell">
                                    <div className="country-info">
                                        <CountryFlagWrapper country={team.country} />
                                        <span className="country-code">{team.country}</span>
                                    </div>
                                </td>
                                {team.racePositions.map((position, index) => {
                                    const points = team.racePoints[index];
                                    return (
                                        <td key={`${team.country}-race-${index}`} className="combined-cell">
                                            {position && points !== null ? (
                                                <>
                                                    <span className="position-part">{position}</span>
                                                    {' '}
                                                    <span className="points-part">{typeof points === 'number' ? `(${points})` : null}</span>
                                                </>
                                            ) : '-'}
                                        </td>
                                    );
                                })}
                                <td className="total-points-cell">
                                    <span className="points-total">{team.totalPoints}</span>
                                    {team.penaltyPoints !== 0 && (
                                        <span className="points-adjustment">({team.penaltyPoints > 0 ? '+' : ''}{team.penaltyPoints})</span>
                                    )}
                                </td>
                                <td className="final-position-cell">
                                    {team.finalPosition}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ResultsTable;